import { cn } from '@/lib/utils'

export interface ResultsCardProps {
  title?: string
  score?: number
  maxScore?: number
  riskLevel?: string
  description?: string
  showPlaceholder?: boolean
  onViewFullResults?: () => void
  className?: string
}

// Helper function to get risk category
const getRiskCategory = (riskLevel: string | undefined): 'low' | 'average' | 'high' => {
  if (!riskLevel) return 'low'
  const normalized = riskLevel.toLowerCase()
  if (normalized === 'low' || normalized === 'no risk' || normalized === 'healthy foundations') return 'low'
  if (normalized === 'moderate' || normalized === 'some attention needed') return 'average'
  if (normalized === 'high' || normalized === 'serious' || normalized === 'serious concerns') return 'high'
  return 'low'
}

// Get recommendation message based on risk level
const getRecommendationMessage = (riskCategory: 'low' | 'average' | 'high') => {
  switch (riskCategory) {
    case 'low':
      return {
        title: 'Looking good!',
        message: 'Your overall assessment indicates a low tendency for violence. You are on the right track! Continue nurturing your healthy relationship patterns.',
        showLink: false,
      }
    case 'average':
      return {
        title: 'Fair enough...',
        message: 'Your overall assessment shows some areas that could use attention. We recommend reviewing our correctional modules to strengthen your relationship.',
        showLink: true,
      }
    case 'high':
      return {
        title: 'Ooops!',
        message: 'Your assessment indicates areas that need improvement. IRIS is here to help you build a healthier, violence-free marriage through our correctional videos.',
        showLink: true,
      }
  }
}

// Get style based on risk category
const getRiskCategoryStyle = (riskCategory: 'low' | 'average' | 'high') => {
  switch (riskCategory) {
    case 'low':
      return { color: '#0ECF5B', iconColor: '#0ECF5B' }
    case 'average':
      return { color: '#FFB319', iconColor: '#FFB319' }
    case 'high':
      return { color: '#FF7043', iconColor: '#FF7043' }
  }
}

export function ResultsCard({
  riskLevel = 'Moderate Risk',
  showPlaceholder = false,
  onViewFullResults,
  className,
}: ResultsCardProps) {
  // Placeholder state when assessment is not completed
  if (showPlaceholder) {
    return (
      <div
        className={cn('rounded-[24px] bg-white p-[34px] w-full min-h-[489px] flex flex-col', className)}
      >
        {/* Header */}
        <div className='mb-8'>
          <h2 className='text-[32px] font-bold text-[#151515] leading-[70px] text-start'>
            Your Results
          </h2>
          <p className='text-[16px] text-[#4e4d4d] font-normal leading-[32px] text-start'>
            Learn about your strengths and areas for growth.{' '}
            <span className='font-bold'>This is a guide, not a judgment.</span>
          </p>
        </div>

        {/* Placeholder Content */}
        <div className='flex-1 flex items-center justify-center'>
          <p className='text-[16px] text-[#4e4d4d] font-normal'>
            Complete your assessment to see results
          </p>
        </div>
      </div>
    )
  }

  const riskCategory = getRiskCategory(riskLevel)
  const recommendation = getRecommendationMessage(riskCategory)
  const riskStyle = getRiskCategoryStyle(riskCategory)

  return (
    <div
      className={cn('rounded-[24px] bg-white p-[34px] w-full min-h-[489px]', className)}
    >
      {/* Header */}
      <div className='mb-6'>
        <h2 className='text-[32px] font-bold text-[#151515] leading-[70px] text-start'>
          Your Results
        </h2>
        <p className='text-[16px] text-[#4e4d4d] font-normal leading-[32px] text-start'>
          Learn about your strengths and areas for growth.{' '}
          <span className='font-bold'>This is a guide, not a judgment.</span>
        </p>
      </div>

      {/* Result Card */}
      <div className='rounded-tl-[12px] rounded-tr-[12px] bg-[#f3f4f6] p-6 relative min-h-[312px] overflow-hidden'>
        {/* Recommendation Title */}
        <div className='flex items-center gap-2 mb-4'>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
              d='M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z'
              fill={riskStyle.iconColor}
            />
          </svg>
          <span className='text-[18px] font-bold leading-[28px]' style={{ color: riskStyle.color }}>
            {recommendation.title}
          </span>
        </div>

        {/* Overall Assessment Label */}
        <h3 className='text-[16px] font-bold text-black leading-[24px] mb-3'>
          Overall Assessment
        </h3>

        {/* Recommendation Message */}
        <p className='text-[14px] leading-[22px] text-[#4e4d4d] text-justify mb-4'>
          {recommendation.message}
        </p>

        {/* Gradient Overlay */}
        <div
          className='absolute inset-x-0 bottom-0 h-[183px] pointer-events-none'
          style={{ background: 'linear-gradient(180deg, rgba(237, 239, 243, 0) 47.58%, rgb(237, 239, 243) 78.39%)' }}
        />

        {/* View Full Results Button */}
        <div className='absolute bottom-[20px] left-1/2 transform -translate-x-1/2'>
          <button
            onClick={onViewFullResults}
            className='h-[44px] w-[172px] rounded-full bg-[#151515] px-[14px] py-[10px] text-[16px] font-bold text-white hover:bg-[#2a2a2a] transition-colors'
          >
            View Full Results
          </button>
        </div>
      </div>
    </div>
  )
}
